import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSendTransfer } from './useSendTransfer';
import { useSession } from './useSession';
import type { Transaction } from '@asia/shared';
import { ApiError, rpcErrorCode } from '../lib/api';
import { TransferSettlementError, waitForTransferSettlement } from '../lib/transfers';
import {
  qrQuoteApi,
  qrToTransaction,
  qrPayApi,
  qrGetApi,
  qrListApi,
  isFinal,
  type QrPayment,
  type QrQuote,
} from '../lib/qr';

export type QrStage = 'idle' | 'quoting' | 'paying' | 'opening' | 'settling';

/**
 * Paying a scanned code: price it, pay for it, wait for confirm, then open the
 * provider payment. After broadcast the transfer is retained so a failed open
 * retries without a second charge (server reclaim on failed/expired rows).
 */
export function useQrPay() {
  const qc = useQueryClient();
  const { send } = useSendTransfer();
  const [stage, setStage] = useState<QrStage>('idle');
  const activeRef = useRef<Promise<QrPayment> | null>(null);
  const paidRef = useRef<{
    link: string;
    amount?: string;
    transferId: string;
    quotedRate: number;
  } | null>(null);

  function pay(params: {
    quote: QrQuote;
    amount?: string;
    walletId: string;
    index: number;
    address: string;
    reauthToken?: string;
  }): Promise<QrPayment> {
    if (activeRef.current) return activeRef.current;
    const task = runPay(params).finally(() => {
      activeRef.current = null;
    });
    activeRef.current = task;
    return task;
  }

  async function runPay(params: {
    quote: QrQuote;
    amount?: string;
    walletId: string;
    index: number;
    address: string;
    reauthToken?: string;
  }): Promise<QrPayment> {
    const { quote, amount, walletId, index, address, reauthToken } = params;
    try {
      let paid = paidRef.current;
      if (paid && (paid.link !== quote.link || paid.amount !== amount)) {
        paid = null;
        paidRef.current = null;
      }

      if (!paid) {
        setStage('quoting');
        const fresh = await qrQuoteApi(quote.link, amount);

        setStage('paying');
        const { transferId } = await send({
          walletId,
          index,
          address,
          to: fresh.payTo,
          amount: fresh.totalUsdt.toFixed(6),
          coin: 'USDT',
          reauthToken,
        });
        if (!transferId) throw new ApiError(502, 'payment_failed');
        paid = {
          link: fresh.link,
          amount,
          transferId,
          quotedRate: fresh.rate,
        };
        paidRef.current = paid;
      }

      setStage('settling');
      try {
        await waitForTransferSettlement(paid.transferId);
      } catch (error) {
        if (error instanceof TransferSettlementError) paidRef.current = null;
        throw error;
      }

      setStage('opening');
      const payment = await qrPayApi({
        link: paid.link,
        ...(paid.amount ? { amount: paid.amount } : {}),
        walletId,
        quotedRate: paid.quotedRate,
        paymentTransferId: paid.transferId,
        idempotencyKey: `qr-${paid.transferId}`,
        reauthToken,
      });

      // Keep paidRef if the open failed terminal-side so Retry reclaims; clear on
      // any non-failed outcome (including in-flight states the UI will poll).
      if (payment.status === 'failed' || payment.status === 'expired') {
        // Server marked refundRequired / failed — retain so user Retry reopens.
      } else {
        paidRef.current = null;
      }

      void qc.invalidateQueries({ queryKey: ['qrPayments'] });
      setStage(isFinal(payment.status) ? 'idle' : 'settling');
      return payment;
    } catch (e) {
      const code = rpcErrorCode(e);
      if (code === 'payment_already_used' || e instanceof TransferSettlementError) {
        paidRef.current = null;
      }
      setStage('idle');
      throw e;
    }
  }

  return { stage, setStage, pay };
}

export function useQrPaymentStatus(id: string | null) {
  return useQuery({
    queryKey: ['qrPayment', id],
    enabled: !!id,
    queryFn: () => qrGetApi(id!),
    refetchInterval: (q) => (q.state.data && isFinal(q.state.data.status) ? false : 2500),
  });
}

export function useQrPayments() {
  const { token } = useSession();
  return useQuery({
    queryKey: ['qrPayments'],
    enabled: !!token,
    queryFn: () => qrListApi(),
    staleTime: 30_000,
  });
}

export function useHistoryWithQr(txs: Transaction[], payments: QrPayment[] | undefined) {
  return useMemo(() => {
    if (!payments?.length) return txs;

    const byHash = new Map<string, QrPayment>();
    for (const p of payments) {
      if (p.txHash) byHash.set(p.txHash.toLowerCase(), p);
    }

    const matched = new Set<string>();
    const merged = txs.map((tx) => {
      const hash = tx.explorerUrl?.split('/').pop()?.toLowerCase();
      const p = hash ? byHash.get(hash) : undefined;
      if (!p) return tx;
      matched.add(p.id);
      return {
        ...tx,
        type: 'qr' as const,
        status: (p.status === 'completed'
          ? 'confirmed'
          : isFinal(p.status)
            ? 'failed'
            : 'pending') as Transaction['status'],
        merchant: p.merchantName ?? undefined,
        mcc: p.mcc ?? undefined,
        paymentType: p.paymentType ?? undefined,
        storeAmount: p.fiatAmount,
        storeCurrency: p.fiatCurrency,
      };
    });

    const unmatched = payments.filter((p) => !matched.has(p.id)).map(qrToTransaction);
    if (!unmatched.length) return merged;
    return [...unmatched, ...merged].sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
  }, [txs, payments]);
}
